import React, { useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';
import { Button } from './components/ui/Button';
import { ThemeProvider, useThemeContext } from './contexts/ThemeContext';
import { SidebarProvider, useSidebarContext } from './contexts/SidebarContext';
import { NavLinksProvider } from './contexts/NavLinksContext';
import { AppWrapper } from './contexts/Storage';
import QueryClientProvider from './providers/QueryClientProvider'
import NavigationTracker from './components/utils/NavigationTracker';
import NavBar from './components/nav/NavBar';
import Sidebar from './components/nav/Sidebar';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import DashboardPage from './pages/DashboardPage';
import NotFoundPage from './pages/NotFoundPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import ThemeToggle from './components/utils/ThemeToggle';
import RecordsPage from './pages/RecordsPage';
import TransactionsPage from './pages/TransactionsPage';
import NewTransactionPage from './pages/NewTransactionPage';
import TransactionEntityPage from './pages/TransactionEntityPage';
import AccountsPage from './pages/AccountsPage';
import CreditPointsPage from './pages/CreditPointsPage';
import ProfilePage from './pages/ProfilePage';
import SubscriptionPage from './pages/SubscriptionPage';
import SubscriptionSuccessPage from './pages/SubscriptionSuccessPage';
import {
  LedgersPage,
  SingleLedgerPage,
  StockValuationPage,
  SingleStockValuationPage,
  OverallStockValuationPage,
  TradingAccountsPage,
  SingleTradingAccountPage,
  OverallTradingAccountPage,
  ProfitLossPage,
  BalanceSheetsPage
} from './pages/accounts';
import ProtectedRoute from './components/auth/ProtectedRoute';

// Component that conditionally renders NavBar and Sidebar based on route
const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { isSidebarOpen } = useSidebarContext();

  const isAuthPage = location.pathname !== '/dashboard' &&
    location.pathname !== '/records' &&
    location.pathname !== '/records/transactions' &&
    location.pathname !== '/records/transaction-entities' &&
    location.pathname !== '/records/accounts' &&
    !location.pathname.startsWith('/records/accounts/') &&
    location.pathname !== '/records/transactions/new' &&
    !location.pathname.startsWith('/records/transactions/edit/') &&
    location.pathname !== '/profile' &&
    location.pathname !== '/credit-points' &&
    location.pathname !== '/subscription' &&
    !location.pathname.startsWith('/subscription/') &&
    // location.pathname !== '/privacy-policy' &&
    location.pathname !== '*';

  if (isAuthPage) {
    return (
      <div className="">
        {children}
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <NavBar />
      <div className="flex flex-1">
        <Sidebar />
        <main
          className="flex-1 transition-all duration-300"
          style={{
            width: '100%',
            maxWidth: '100%',
            overflowX: 'hidden'
          }}
        >
          <div className="">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

// Component that uses the theme and handles routing
const AppContent: React.FC = () => {
  const { isDark } = useThemeContext();
  return (
    <Router>
      <NavigationTracker />
      <Layout>
        <Routes>
          <Route path="/" element={<LoginPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />

          {/* Protected Routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          } />
          <Route path="/records" element={
            <ProtectedRoute>
              <RecordsPage />
            </ProtectedRoute>
          } />
          <Route path="/records/transactions" element={
            <ProtectedRoute>
              <TransactionsPage />
            </ProtectedRoute>
          } />
          <Route path="/records/transactions/new" element={
            <ProtectedRoute>
              <NewTransactionPage />
            </ProtectedRoute>
          } />
          <Route path="/records/transactions/edit/:recordId/:transactionId" element={
            <ProtectedRoute>
              <NewTransactionPage />
            </ProtectedRoute>
          } />
          <Route path="/records/transaction-entities" element={
            <ProtectedRoute>
              <TransactionEntityPage />
            </ProtectedRoute>
          } />
          <Route path="/records/accounts" element={
            <ProtectedRoute>
              <AccountsPage />
            </ProtectedRoute>
          } />
          {/* Accounts Routes under records */}
          <Route path="/records/accounts/ledgers" element={
            <ProtectedRoute>
              <LedgersPage />
            </ProtectedRoute>
          } />
          <Route path="/records/accounts/ledgers/:entityId" element={
            <ProtectedRoute>
              <SingleLedgerPage />
            </ProtectedRoute>
          } />
          <Route path="/records/accounts/stock-valuation" element={
            <ProtectedRoute>
              <StockValuationPage />
            </ProtectedRoute>
          } />
          <Route path="/records/accounts/stock-valuation/:entityId" element={
            <ProtectedRoute>
              <SingleStockValuationPage />
            </ProtectedRoute>
          } />
          <Route path="/records/accounts/overall-stock-valuation" element={
            <ProtectedRoute>
              <OverallStockValuationPage />
            </ProtectedRoute>
          } />
          <Route path="/records/accounts/trading-accounts" element={
            <ProtectedRoute>
              <TradingAccountsPage />
            </ProtectedRoute>
          } />
          <Route path="/records/accounts/trading-accounts/:entityId" element={
            <ProtectedRoute>
              <SingleTradingAccountPage />
            </ProtectedRoute>
          } />
          <Route path="/records/accounts/overall-trading-account" element={
            <ProtectedRoute>
              <OverallTradingAccountPage />
            </ProtectedRoute>
          } />
          <Route path="/records/accounts/profit-loss" element={
            <ProtectedRoute>
              <ProfitLossPage />
            </ProtectedRoute>
          } />
          <Route path="/records/accounts/balance-sheets" element={
            <ProtectedRoute>
              <BalanceSheetsPage />
            </ProtectedRoute>
          } />
          <Route path="/profile" element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          } />
          <Route path="/credit-points" element={
            <ProtectedRoute>
              <CreditPointsPage />
            </ProtectedRoute>
          } />
          <Route path="/subscription" element={
            <ProtectedRoute>
              <SubscriptionPage />
            </ProtectedRoute>
          } />
          <Route path="/subscription/success" element={
            <ProtectedRoute>
              <SubscriptionSuccessPage />
            </ProtectedRoute>
          } />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Layout>

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />
    </Router>
  );
};

// Main App component that wraps everything with ThemeProvider
const App: React.FC = () => {
  return (
    <QueryClientProvider>
      <AppWrapper>
        <ThemeProvider>
          <SidebarProvider>
            <NavLinksProvider>
              <AppContent />
            </NavLinksProvider>
          </SidebarProvider>
        </ThemeProvider>
      </AppWrapper>
    </QueryClientProvider>
  );
};

export default App;
